/**
 * Admin → Users (super-admin only).
 *
 * Lists all admin users, lets super-admin add new, change role, reset password,
 * activate/deactivate, or delete. The "admin" role can still see the link in
 * the nav but the API will return 403 if they try to act.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('adminToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const AdminUsers = () => {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'admin' });
  const [resetEmail, setResetEmail] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  const refresh = async () => {
    const r = await fetch(`${API}/api/admin/users`, { headers: authHeaders() });
    if (r.ok) setUsers((await r.json()).users || []);
    else if (r.status === 403) setMsg({ ok: false, text: 'Super-admin role required to manage users.' });
  };

  useEffect(() => {
    fetch(`${API}/api/admin/me`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then(setMe);
    refresh();
  }, []);

  const create = async () => {
    if (!form.email || !form.password || !form.name) return;
    setBusy('create');
    setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setForm({ email: '', name: '', password: '', role: 'admin' });
        setMsg({ ok: true, text: `Created ${form.email} as ${form.role}` });
        refresh();
      } else {
        setMsg({ ok: false, text: d.detail || 'Create failed' });
      }
    } finally { setBusy(null); }
  };

  const reset = async () => {
    if (!resetEmail || !resetPassword) return;
    setBusy('reset');
    try {
      const r = await fetch(`${API}/api/admin/users/${encodeURIComponent(resetEmail)}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ new_password: resetPassword }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setMsg({ ok: true, text: `Password reset for ${resetEmail}` });
        setResetEmail(null);
        setResetPassword('');
      } else {
        setMsg({ ok: false, text: d.detail || 'Reset failed' });
      }
    } finally { setBusy(null); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Delete admin user ${email}? This cannot be undone.`)) return;
    setBusy(`del-${email}`);
    try {
      const r = await fetch(`${API}/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (r.ok) {
        setMsg({ ok: true, text: `Deleted ${email}` });
        refresh();
      }
    } finally { setBusy(null); }
  };

  const toggleActive = async (u) => {
    setBusy(`act-${u.email}`);
    try {
      await fetch(`${API}/api/admin/users/${encodeURIComponent(u.email)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ active: !u.active }),
      });
      refresh();
    } finally { setBusy(null); }
  };

  const isSuper = me?.role === 'super_admin';

  return (
    <div className="p-6 max-w-5xl" data-testid="admin-users-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Admin Users</h1>
        <p className="text-gray-600 mt-2">
          Manage admin and super-admin accounts. Super-admins can add, edit, reset passwords, and delete users.
        </p>
      </div>

      {me && !isSuper && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-6 text-sm text-amber-800">
          You're logged in as <strong>{me?.role}</strong>. Only super-admins can modify users.
        </div>
      )}

      {isSuper && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold mb-3">Add Admin User</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input
              data-testid="new-user-email"
              type="email"
              placeholder="email@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <input
              data-testid="new-user-name"
              type="text"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <input
              data-testid="new-user-password"
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <select
              data-testid="new-user-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="admin">admin</option>
              <option value="super_admin">super_admin</option>
            </select>
          </div>
          <button
            data-testid="create-user-btn"
            onClick={create}
            disabled={busy === 'create' || !form.email || !form.password || !form.name}
            className="mt-3 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded font-semibold text-sm disabled:bg-gray-300"
          >
            {busy === 'create' ? 'Adding…' : 'Add User'}
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Email</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Name</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Role</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Active</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Created</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.email} className="border-b hover:bg-gray-50">
                <td className="py-2 px-3 text-sm font-mono">{u.email}</td>
                <td className="py-2 px-3 text-sm">{u.name}</td>
                <td className="py-2 px-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                    u.role === 'super_admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="py-2 px-3 text-sm">{u.active === false ? '🔒 Disabled' : '✅'}</td>
                <td className="py-2 px-3 text-xs text-gray-400">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="py-2 px-3 text-xs">
                  {isSuper && (
                    <div className="flex gap-2">
                      <button
                        data-testid={`reset-${u.email}`}
                        onClick={() => setResetEmail(u.email)}
                        className="text-teal-600 hover:underline"
                      >
                        Reset Pwd
                      </button>
                      <button
                        data-testid={`toggle-${u.email}`}
                        onClick={() => toggleActive(u)}
                        disabled={busy === `act-${u.email}`}
                        className="text-amber-600 hover:underline"
                      >
                        {u.active === false ? 'Activate' : 'Disable'}
                      </button>
                      {u.email !== me?.email && (
                        <button
                          data-testid={`delete-${u.email}`}
                          onClick={() => remove(u.email)}
                          disabled={busy === `del-${u.email}`}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resetEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setResetEmail(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-[90%] max-w-md" onClick={(e) => e.stopPropagation()} data-testid="reset-modal">
            <h3 className="text-xl font-bold mb-3">Reset password for {resetEmail}</h3>
            <input
              data-testid="reset-pwd-input"
              type="password"
              placeholder="New password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setResetEmail(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm">Cancel</button>
              <button
                data-testid="confirm-reset-btn"
                onClick={reset}
                disabled={busy === 'reset' || !resetPassword}
                className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded text-sm font-semibold disabled:bg-gray-300"
              >
                {busy === 'reset' ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {msg && (
        <div className={`fixed bottom-4 right-4 p-3 rounded shadow-lg ${msg.ok ? 'bg-emerald-600' : 'bg-red-600'} text-white text-sm`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
